import { ClipboardCopyIcon } from '@radix-ui/react-icons';
import { Badge, Flex, IconButton, Text } from '@radix-ui/themes';
import { CsvExportHelper } from 'classes/helpers/csv-export.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonTableHoC } from 'components/common/table';
import { SessionsToolbar } from 'components/sections/analytics/session-history/toolbar';
import { SessionEventsContext } from 'contexts/session-events.context';
import { format } from 'date-fns-tz';
import parseISO from 'date-fns/parseISO';
import { SessionDialogMode } from 'enums/session.enums';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATETIME_FORMAT_SHORT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { useContext, useMemo } from 'react';
import { MachinesService } from 'services/machines.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';

const ENABLE_EDIT_SESSION = false;

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface ISessionSummaryRow extends ISessionSummary, RowWithID {}

export const SessionsTable = () => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const { sessions, loading, getPitchStatsData, getShotsData, showDialog } =
    useContext(SessionEventsContext);

  const rows: ISessionSummaryRow[] = useMemo(
    () => StringHelper.addIds(sessions, 'sessionID'),
    [sessions]
  );

  const columns = useMemo<ITypedTableColumn<ISessionSummaryRow>[]>(
    () => [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        actions: [
          {
            label: 'Visualize',
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: () => !userRoleFeatureFlags.analytics.visualize,
            disableFn: () => loading,
            onClick: (m) =>
              showDialog({
                mode: SessionDialogMode.visualize,
                session: m,
                key: Date.now(),
              }),
          },
          {
            label: 'common.edit',
            invisibleFn: () => !ENABLE_EDIT_SESSION,
            disableFn: () => loading,
            onClick: (m) =>
              showDialog({
                mode: SessionDialogMode.edit,
                session: m,
                key: Date.now(),
              }),
          },
          {
            label: 'common.export-shots',
            disableFn: () => loading,
            onClick: async (m) => {
              const { machine } =
                await MachinesService.getInstance().getByMachineID(m.machineID);

              const data = await getShotsData({
                machineID: m.machineID,
                session: m.sessionID,
                debugTracking:
                  current.role === UserRole.admin ||
                  machine.enable_tracking_debug,
                szType: machine.strikeZoneType,
              });

              if (data.length === 0) {
                NotifyHelper.warning({
                  message_md: t('common.session-has-no-shots'),
                });
                return;
              }

              CsvExportHelper.saveCsv(data, {
                prefix: slugify(
                  [
                    m.machineID,
                    m.nickname,
                    format(parseISO(m.startDate), LOCAL_DATETIME_FORMAT_SHORT, {
                      timeZone: LOCAL_TIMEZONE,
                    }),
                  ]
                    .map((m) => m?.trim())
                    .filter((m) => m)
                    .join('-'),
                  { strict: true }
                ),
              });
            },
          },
          {
            label: 'common.export-stats',
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: () => !userRoleFeatureFlags.analytics.exportStats,
            disableFn: () => loading,
            onClick: async (m) => {
              const data = await getPitchStatsData({
                session: m.sessionID,
              });

              if (data.length === 0) {
                NotifyHelper.warning({
                  message_md: t('common.session-has-no-pitch-stats'),
                });
                return;
              }

              CsvExportHelper.saveCsv(data, {
                prefix: `session-${m.sessionID}_stats`,
              });
            },
          },
        ],
      },
      {
        label: 'common.machine',
        key: 'machineID',
        tooltipFn: (s) => (s.nickname ? `aka: ${s.nickname}` : undefined),
        formatFn: (s) => <Badge>{s.machineID}</Badge>,
      },
      {
        label: 'common.identifier',
        key: 'sessionID',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.sessionID, b.sessionID) * dir,
        formatFn: (s) => (
          <Flex gap="1" align="center">
            <Text className="font-mono">
              *-{s.sessionID.split('-').slice(-1)}
            </Text>
            <IconButton
              className="btn-floating"
              color={RADIX.COLOR.NEUTRAL}
              variant={RADIX.BUTTON.VARIANT.BORDERLESS}
              onClick={(e) => {
                e.stopPropagation();

                navigator.clipboard.writeText(s.sessionID);

                NotifyHelper.success({
                  message_md: t('common.x-copied-to-clipboard', {
                    x: `"${s.sessionID}"`,
                  }),
                });
              }}
            >
              <ClipboardCopyIcon />
            </IconButton>
          </Flex>
        ),
      },
      {
        label: 'common.name',
        key: 'sessionName',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.sessionName, b.sessionName) * dir,
      },
      {
        label: 'common.shots',
        key: 'firesOutGame',
        align: 'right',
      },
      {
        label: 'common.start-date',
        key: 'startDate',
        dataType: 'datetime',
      },
    ],
    [userRoleFeatureFlags, loading]
  );

  /** sessions table */
  const pagination: ITablePageable = {
    paginationID: PaginationID.SessionList,
    total: sessions.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'start',
      dir: 1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
  };

  return (
    <CommonTableHoC
      dataTestID="SessionHistory"
      identifier="SessionHistoryTable"
      displayColumns={columns}
      displayData={rows}
      toolbarContent={<SessionsToolbar />}
      loading={loading}
      {...pagination}
      {...select}
      {...sort}
      vFlex
    />
  );
};
